import React, { useEffect, useState } from 'react'
import { useBenefits } from '../BenefitsProvider'
import {
  TextField,
  InputField,
  SelectField,
} from '~/components/shared/FormFields'
import { Button } from '~/lib/ui/button'
import { useTranslation } from '~/hooks/useTranslation'
import ResumeUpload from './ResumeUpload'
import { ServiceRequestForm } from './RequestForms'
import TabNav from '~/components/shared/TabNav/TabNav'
import { useIsMobile } from '~/hooks/useMediaQuery'
import TabButton from '~/components/shared/TabNav/TabButton'
import ButtonContainer from '~/components/shared/TabNav/ButtonContainer'
import { PanelContainer, TabPanel } from '~/components/shared/TabNav/index'
import { FormMessage } from '~/lib/ui/form'
import SavedStatusIcon from '~/components/shared/SavedStatusIcon'

const ResumeReviewForm = () => {
  const { t } = useTranslation('schedule_modal.resume_review')
  const {
    control,
    message,
    createServiceRequest,
    meetingViaCalendly,
    seniorityLevel,
    coach,
    resumeReview,
    createRevisionRequest,
    notes,
    resumeId,
    uploadedResumeId,
    resumeRevisions,
    remote_content,
    submitted,
    select,
    current_user,
  } = useBenefits()

  const revisionRequest = resumeReview !== null

  const userHasSystemResume = current_user.candidate_resumes?.length > 0

  const introText = revisionRequest
    ? t('intro_text_revision')
    : userHasSystemResume
      ? t('intro_text')
      : t('intro_text_no_system_resume')

  const variant = select ? 'default' : 'coachcta'

  const noDocument = !(resumeId || uploadedResumeId) && !revisionRequest

  const revisionRequestUnsubmittable =
    !notes || (!remote_content && !revisionRequest) || submitted

  const platformReviewDisabled = revisionRequest
    ? revisionRequestUnsubmittable
    : !(message && seniorityLevel) || submitted

  const meetingRequestButtonDisabled = revisionRequest
    ? revisionRequestUnsubmittable
    : (!message && !(uploadedResumeId || resumeId) && !seniorityLevel) ||
      submitted

  const requestReviewButtonText = revisionRequest
    ? t('request_final_review')
    : t('request_review')

  const [loading, setLoading] = useState(submitted)
  const handleRequestReviewButtonClick = () => {
    setLoading(true)
    if (revisionRequest) {
      createRevisionRequest()
    } else {
      createServiceRequest()
    }
  }

  return (
    <ServiceRequestForm>
      <div className="text-lg">{introText}</div>
      {!revisionRequest && (
        <FormTabNav t={t} revisionRequest={revisionRequest} />
      )}
      {!revisionRequest && (
        <SelectField
          control={control}
          name="seniority_level"
          label={t('seniority_level_label')}
          choices={[
            {
              value: 'Entry to Mid Level',
              label: t('entry_level'),
            },
            {
              value: 'Manager to Director Level',
              label: t('manager_level'),
            },
            {
              value: 'VP to C Level',
              label: t('vp_level'),
            },
          ]}
        />
      )}
      <TextField
        control={control}
        name={revisionRequest ? 'notes' : 'message'}
        label={t('message_label')}
      />
      {revisionRequest && (
        <FormTabNav t={t} revisionRequest={revisionRequest} />
      )}
      {!revisionRequest && (
        <InputField
          control={control}
          name="example_job_1"
          label={t('example_jobs')}
          description={t('example_jobs_description')}
        />
      )}
      <div className="flex justify-center">
        <div>
          {revisionRequest ? (
            <div className="flex flex-col items-center justify-center gap-4">
              {/* Tooltip wrapper */}
              <div
                className="relative"
                data-toggle="tooltip"
                data-placement="top"
              >
                <Button
                  type="button"
                  onClick={handleRequestReviewButtonClick}
                  variant={select ? 'default' : 'coachcta'}
                  disabled={
                    loading ||
                    (revisionRequest
                      ? revisionRequestUnsubmittable
                      : !(message && seniorityLevel) || submitted)
                  } // Disable th
                >
                  {t('request_platform_review')}
                </Button>
              </div>
              {resumeRevisions.length <= 2 && (
                <>
                  <div className="font-medium">{t('or')}</div>
                  <div
                    className="relative"
                    data-toggle="tooltip"
                    data-placement="top"
                  >
                    <Button
                      variant={variant}
                      disabled={noDocument || meetingRequestButtonDisabled}
                    >
                      {t('schedule_call_30_minute')}
                      <SavedStatusIcon
                        loadingStatus={
                          loading || submitted ? t('loading') : null
                        }
                      />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : !meetingViaCalendly || !coach || coach.isLiaison ? (
            <Button
              type="button"
              onClick={handleRequestReviewButtonClick}
              variant={variant}
              disabled={noDocument || platformReviewDisabled}
            >
              {requestReviewButtonText}
              <SavedStatusIcon
                loadingStatus={loading || submitted ? t('loading') : null}
              />
            </Button>
          ) : (
            <Button
              variant={variant}
              disabled={noDocument || meetingRequestButtonDisabled}
            >
              {t('next_button')}
              <SavedStatusIcon
                loadingStatus={loading || submitted ? t('loading') : null}
              />
            </Button>
          )}
        </div>
      </div>
      <FormMessage />
    </ServiceRequestForm>
  )
}

export default ResumeReviewForm

const Wrapper = ({ children }) => {
  const isMobile = useIsMobile()
  if (isMobile) return children

  return (
    <div className="relative w-full overflow-x-scroll sm:static sm:overflow-auto">
      {children}
    </div>
  )
}

const FormTabNav = ({ t, revisionRequest }) => {
  const { control, current_user, setUploadedResumeId, serviceRequestForm } =
    useBenefits()
  const userHasSystemResume = current_user.candidate_resumes?.length > 0
  const allowSystemResume = userHasSystemResume && !revisionRequest
  return (
    <TabNav defaultActiveTab={'upload_resume'}>
      {allowSystemResume && <TabNavButtons />}
      <PanelContainer asChild>
        <TabPanel name={'upload_resume'}>
          <UploadResume
            containerClassName="px-0 pb-0"
            resumeReview={true}
            setUploadedResumeId={setUploadedResumeId}
            revisionRequest={revisionRequest}
            serviceRequestForm={serviceRequestForm}
            className="min-h-[10rem] border-black"
            helpText={t('dropzone_help_text')}
          />
        </TabPanel>
        <TabPanel name="choose_resume">
          <SelectResume
            control={control}
            name="resume_document_id"
            label={t('select_resume_label')}
            choices={current_user.candidate_resumes}
            setUploadedResumeId={setUploadedResumeId}
          />
        </TabPanel>
      </PanelContainer>
    </TabNav>
  )
}

const UploadResume = ({ ...props }) => {
  const { setValue } = useBenefits()
  useEffect(() => {
    setValue('resume_document_id', null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return <ResumeUpload {...props} />
}

const SelectResume = ({ setUploadedResumeId, ...props }) => {
  useEffect(() => {
    setUploadedResumeId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return <SelectField {...props} />
}
const TabNavButtons = () => {
  const isMobile = useIsMobile()
  const titleClass = isMobile ? '' : 'text-lg mx-0'

  const { t } = useTranslation('schedule_modal.resume_review')

  return (
    <Wrapper>
      <ButtonContainer
        carouselWrapperClass="min-h-[4.5rem] mb-8"
        className="mb-6 flex flex-row justify-between gap-5 min-[530px]:justify-start min-[530px]:gap-10"
        buttonClass="min-w-16"
        titleClass={titleClass}
      >
        <TabButton name={'upload_resume'} text={t('upload_resume')} />
        <TabButton name={'choose_resume'} text={t('choose_resume')} />
      </ButtonContainer>
    </Wrapper>
  )
}
